import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed } from 'vue'

import {
  getSuperAdmins,
  inviteSuperAdmins,
  removeSuperAdmin,
  resendSuperAdminInvitation,
  transferOwnership,
} from '@/features/admin/platform-setup/api'

export const SUPER_ADMINS_QUERY_KEY = 'super-admins'

/** Super admins de la plateforme : liste, invitation, relance, retrait et transfert de propriété. */
export default function useSuperAdmins() {
  const queryClient = useQueryClient()
  const query = useQuery({ queryKey: [SUPER_ADMINS_QUERY_KEY], queryFn: getSuperAdmins })
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [SUPER_ADMINS_QUERY_KEY] })

  const inviteMutation = useMutation({ mutationFn: inviteSuperAdmins, onSuccess: invalidate })
  const resendMutation = useMutation({ mutationFn: resendSuperAdminInvitation, onSuccess: invalidate })
  const removeMutation = useMutation({ mutationFn: removeSuperAdmin, onSuccess: invalidate })
  const transferMutation = useMutation({ mutationFn: transferOwnership, onSuccess: invalidate })

  const superAdminsRef = computed(() => query.data.value?.superAdmins ?? [])
  const canTransferRef = computed(() => !!query.data.value?.canTransfer)
  const ownerRef = computed(() => superAdminsRef.value.find((admin) => admin.isOwner) ?? null)
  // L'étape « Équipe » est faite dès qu'au moins une autre personne a été invitée.
  const hasTeamRef = computed(() => superAdminsRef.value.some((admin) => !admin.isCurrentUser))

  return {
    superAdminsRef,
    hasTeamRef,
    isLoadingRef: query.isLoading,
    isErrorRef: query.isError,
    invite: inviteMutation.mutateAsync,
    isInvitingRef: inviteMutation.isPending,
    resend: resendMutation.mutateAsync,
    remove: removeMutation.mutateAsync,
    canTransferRef,
    ownerRef,
    transfer: transferMutation.mutateAsync,
    isTransferringRef: transferMutation.isPending,
  }
}
